import * as React from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import i18n from 'i18next';
import type { TenantBranding } from '@senviro/core-api-client';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { defaultBranding } from './defaults';
import { applyTheme } from './applyTheme';

/**
 * Tenant-branding context (W5-F1-S1).
 *
 * Fetches the active `tenant_branding` row from the apps/api proxy at
 * `GET /api/v1/proxy/core/tenant-branding`. The proxy is caller's-org-implicit
 * (org resolved from the JWT per Core ADR 0009), so this hook takes no
 * arguments. When the proxy returns `null` (Core 404 — no branding row
 * yet) or any other failure, the provider falls back to `defaultBranding`
 * so the app shell always has a renderable value.
 *
 * Cache key: `['tenant-branding', tid]`. The `tid` salt ensures a tenant
 * switch (rare — usually a separate device/login) re-fetches.
 *
 * Foundational for W5-F1-S2 (CSS-variable theming), W5-F1-S3 (logo +
 * favicon), W5-F1-S4 (i18n product-name), and every downstream W5
 * feature that reads from this context.
 */

export interface TenantBrandingContextValue {
  /** The current branding (defaults if not yet loaded or on 404). */
  branding: TenantBranding;
  /** True until the first fetch settles — useful for skeleton states. */
  isLoading: boolean;
  /** True when the proxy errored AND we fell back to defaults. */
  isFallback: boolean;
  /** Underlying TanStack Query result for advanced consumers. */
  query: UseQueryResult<TenantBranding | null>;
}

const TenantBrandingContext =
  React.createContext<TenantBrandingContextValue | null>(null);

export const TENANT_BRANDING_QUERY_KEY = (
  tid: string | null,
): readonly unknown[] => ['tenant-branding', tid] as const;

/** Five minutes — matches the analytics/dashboard hooks. */
const STALE_TIME_MS = 5 * 60 * 1000;

export function TenantBrandingProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { account, getAccessToken } = useAuth();
  const tid = resolveTid(account);

  const query = useQuery<TenantBranding | null>({
    queryKey: TENANT_BRANDING_QUERY_KEY(tid),
    enabled: tid != null,
    staleTime: STALE_TIME_MS,
    queryFn: async () => {
      // apps/api wraps responses in `{ data }`. When data is null (no
      // branding row yet, Core 404), apiFetch's `wrapped?.data ?? parsed`
      // unwrap returns the whole envelope instead of `null`. Normalise
      // here so the consumer surface is always `TenantBranding | null`.
      const raw = await apiFetch<TenantBranding | { data: null } | null>(
        '/api/v1/proxy/core/tenant-branding',
        { getAccessToken },
      );
      if (
        raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        (raw as { data: unknown }).data === null
      ) {
        return null;
      }
      return raw as TenantBranding | null;
    },
  });

  const value = React.useMemo<TenantBrandingContextValue>(() => {
    const branding = query.data ?? defaultBranding;
    return {
      branding,
      isLoading: query.isLoading,
      isFallback: query.data == null && !query.isLoading,
      query,
    };
  }, [query]);

  // W5-F1-S2 — write the branding's CSS variables onto :root whenever the
  // resolved branding changes. Skipped during the very first render when the
  // query is still loading and the branding is the default (the :root
  // defaults from globals.css are already correct).
  // W5-F1-S4 — also push the partner's product_name into the i18n bundle
  // so every `t('app.name')` callsite reflects the partner brand. We
  // override the existing `app.name` key (used by Topbar / Sidebar / page
  // titles) rather than adding a parallel `app.product_name` — the existing
  // key is already wired into every consumer; overriding is zero-churn.
  React.useEffect(() => {
    applyTheme(value.branding);
    const productName = value.branding.product_name || 'Senviro';
    for (const lng of ['en', 'nb']) {
      // `addResource` is idempotent and safe to call repeatedly. The
      // `silent: true` flag suppresses the "translation overridden"
      // dev-mode warning that's noisy at every branding fetch.
      i18n.addResource(lng, 'common', 'app.name', productName, {
        silent: true,
      });
      i18n.addResource(lng, 'common', 'app.product_name', productName, {
        silent: true,
      });
    }
    // react-i18next caches translations per-component via the `useTranslation`
    // hook; bumping the language back to itself is the cheapest way to fan
    // out a re-render across every t-callsite when the brand product_name
    // changes mid-session. No-op when language hasn't actually changed.
    void i18n.changeLanguage(i18n.language);
  }, [value.branding]);

  return (
    <TenantBrandingContext.Provider value={value}>
      {children}
    </TenantBrandingContext.Provider>
  );
}

/**
 * Read the current tenant branding. Always returns a non-null
 * `TenantBranding` — the provider guarantees the defaults fallback so
 * consumers never branch on `null`.
 */
export function useTenantBranding(): TenantBrandingContextValue {
  const ctx = React.useContext(TenantBrandingContext);
  if (!ctx) {
    throw new Error(
      'useTenantBranding() must be used inside <TenantBrandingProvider>',
    );
  }
  return ctx;
}

/**
 * Non-throwing variant of {@link useTenantBranding}. Returns `null` when no
 * `<TenantBrandingProvider>` is mounted instead of throwing, so
 * provider-agnostic side-effect hooks (e.g. `useRouteMeta`, #1716) can run
 * in isolated unit tests / pre-provider surfaces and fall back to the
 * platform defaults. Inside the app the provider always wraps the router,
 * so this returns the live value.
 */
export function useOptionalTenantBranding(): TenantBrandingContextValue | null {
  return React.useContext(TenantBrandingContext);
}

/**
 * Best-effort tenant-id extraction from the MSAL or dev account. Returns
 * `null` when the account isn't loaded yet — TanStack Query keeps the
 * fetch disabled in that case.
 */
function resolveTid(
  account: ReturnType<typeof useAuth>['account'],
): string | null {
  if (!account) return null;
  // MSAL `AccountInfo.tenantId` and the dev fixture both expose `tenantId`.
  const maybe = (account as { tenantId?: unknown }).tenantId;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : null;
}
